import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Plus, X, Users, Home, Handshake, CalendarClock, ChevronLeft, ChevronRight, Trash2, StickyNote, Sparkles, Target, Mail, QrCode, Copy, Check } from "lucide-react";
import { crmDashboard, crmLeads, crmCreateLead, crmUpdateLead, crmAddNote, crmDeleteLead, crmToday, crmLeadMatches, crmLeadMarketing, crmDraftEmail } from "@/lib/api";
import { useAuth, login } from "@/context/AuthContext";

const STAGES = [
  { id: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { id: "qualified", label: "Qualified", color: "bg-indigo-100 text-indigo-700" },
  { id: "consultation", label: "Consultation", color: "bg-violet-100 text-violet-700" },
  { id: "showing", label: "Showing", color: "bg-amber-100 text-amber-700" },
  { id: "offer", label: "Offer", color: "bg-orange-100 text-orange-700" },
  { id: "closed", label: "Closed", color: "bg-emerald-100 text-emerald-700" },
];
const TYPE_LABEL = { buyer: "Buyer", seller: "Seller", renter: "Renter", other: "Other" };
const idx = (s) => STAGES.findIndex((x) => x.id === s);

export default function AgentCRM() {
  const { user, loading: authLoading } = useAuth();
  const [dash, setDash] = useState(null);
  const [today, setToday] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [active, setActive] = useState(null); // lead detail

  const refresh = useCallback(async () => {
    const [d, l, t] = await Promise.all([crmDashboard(), crmLeads(), crmToday()]);
    setDash(d); setLeads(l.leads || []); setToday(t); setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    refresh().catch(() => setLoading(false));
  }, [user, authLoading, refresh]);

  const move = async (lead, dir) => {
    const ni = idx(lead.stage) + dir;
    if (ni < 0 || ni >= STAGES.length) return;
    const stage = STAGES[ni].id;
    setLeads((ls) => ls.map((x) => (x.id === lead.id ? { ...x, stage } : x)));
    try { await crmUpdateLead(lead.id, { stage }); refresh(); }
    catch { toast.error("Couldn't move lead"); refresh(); }
  };

  if (!user && authLoading) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;
  if (!user) return (
    <Center><Lock className="h-10 w-10 text-slate-300" /><h1 className="mt-4 text-2xl font-black text-[#0F172A]">Sign in to open your CRM</h1>
      <button onClick={() => login("/homes/crm")} className="mt-5 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white hover:bg-[#1D4ED8]" data-testid="crm-signin">Sign in</button></Center>
  );
  if (loading || !dash) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;

  const c = dash.counts;
  return (
    <div className="mx-auto max-w-[1400px] px-4 py-8" data-testid="agent-crm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/homes/agent-portal" className="text-sm text-[#2563EB]">← Agent Portal</Link>
          <h1 className="text-3xl font-black text-[#0F172A]">My Business</h1>
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="crm-add-lead">
          <Plus className="h-4 w-4" /> Add lead
        </button>
      </div>

      {/* Dashboard counts */}
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="crm-counts">
        <Kpi icon={Users} label="Leads" value={c.leads} />
        <Kpi icon={Users} label="Buyers" value={c.buyers} />
        <Kpi icon={Users} label="Sellers" value={c.sellers} />
        <Kpi icon={Users} label="Renters" value={c.renters} />
        <Kpi icon={Home} label="Listings" value={c.listings} />
        <Kpi icon={Handshake} label="Deals" value={c.deals} />
      </div>

      {/* Today — who to contact (AI assistant, rule-based) */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-today">
        <div className="flex items-center gap-2 text-sm font-bold text-[#0F172A]"><Sparkles className="h-4 w-4 text-[#2563EB]" /> Who to contact today ({today?.count || 0})</div>
        {today?.items?.length ? (
          <div className="mt-3 space-y-2">
            {today.items.map((i) => {
              const lead = leads.find((l) => l.id === i.id);
              const dot = i.priority === 1 ? "bg-rose-500" : i.priority === 2 ? "bg-orange-500" : i.priority === 3 ? "bg-emerald-500" : "bg-slate-400";
              return (
                <button key={i.id} onClick={() => lead && setActive(lead)} className="flex w-full items-center gap-3 rounded-xl border border-slate-100 px-3 py-2 text-left hover:bg-slate-50" data-testid={`today-${i.id}`}>
                  <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
                  <span className="font-bold text-[#0F172A]">{i.name}</span>
                  <span className="text-xs text-slate-400">{TYPE_LABEL[i.type]}</span>
                  <span className="ml-auto text-xs font-semibold text-slate-500">{i.reason}</span>
                </button>
              );
            })}
          </div>
        ) : <p className="mt-2 text-sm text-slate-500">Nothing urgent. You're all caught up.</p>}
      </div>

      {/* Pipeline board */}
      <div className="mt-5 flex gap-4 overflow-x-auto pb-4" data-testid="crm-pipeline">
        {STAGES.map((s) => {
          const items = leads.filter((l) => l.stage === s.id);
          return (
            <div key={s.id} className="w-[280px] shrink-0" data-testid={`pipeline-col-${s.id}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${s.color}`}>{s.label}</span>
                <span className="text-xs font-semibold text-slate-400">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((l) => (
                  <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`lead-card-${l.id}`}>
                    <button onClick={() => setActive(l)} className="block w-full text-left">
                      <div className="font-bold text-[#0F172A]">{l.name}</div>
                      <div className="text-xs text-slate-500">{TYPE_LABEL[l.type]} · {l.source}{l.source_detail ? ` (${l.source_detail})` : ""}</div>
                      {l.follow_up_date && <div className="mt-1 text-[11px] font-semibold text-orange-600">Follow-up {l.follow_up_date.slice(0, 10)}</div>}
                    </button>
                    <div className="mt-2 flex items-center justify-between">
                      <button onClick={() => move(l, -1)} disabled={idx(l.stage) === 0} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30" data-testid={`lead-prev-${l.id}`}><ChevronLeft className="h-4 w-4" /></button>
                      <button onClick={() => move(l, 1)} disabled={idx(l.stage) === STAGES.length - 1} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30" data-testid={`lead-next-${l.id}`}><ChevronRight className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">No leads</div>}
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && <AddLead onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); refresh(); }} />}
      {active && <LeadDetail lead={active} onClose={() => setActive(null)} onChanged={() => refresh()} setActive={setActive} />}
    </div>
  );
}

function AddLead({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", type: "buyer", source: "manual", follow_up_date: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try { await crmCreateLead(f); toast.success("Lead added"); onSaved(); }
    catch { toast.error("Couldn't add lead"); setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Add lead" testid="add-lead-modal">
      <div className="space-y-3">
        <Inp label="Name *" v={f.name} onChange={set("name")} testid="add-lead-name" />
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Email" v={f.email} onChange={set("email")} testid="add-lead-email" />
          <Inp label="Phone" v={f.phone} onChange={set("phone")} testid="add-lead-phone" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Type" v={f.type} onChange={set("type")} opts={Object.entries(TYPE_LABEL)} testid="add-lead-type" />
          <Inp label="Follow-up date" type="date" v={f.follow_up_date} onChange={set("follow_up_date")} testid="add-lead-followup" />
        </div>
        <button onClick={save} disabled={busy} className="w-full rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#1D4ED8] disabled:opacity-60" data-testid="add-lead-save">
          {busy ? "Saving…" : "Save lead"}
        </button>
      </div>
    </Modal>
  );
}

function LeadDetail({ lead, onClose, onChanged, setActive }) {
  const [note, setNote] = useState("");
  const [fu, setFu] = useState(lead.follow_up_date?.slice(0, 10) || "");
  const c0 = lead.criteria || {};
  const [crit, setCrit] = useState({
    budget_max: c0.budget_max || "", beds_min: c0.beds_min || "", baths_min: c0.baths_min || "",
    must_haves: (c0.must_haves || []).join(", "),
    city_geo_ids: c0.city_geo_ids || [],
  });
  const [matches, setMatches] = useState(null);
  const [mkt, setMkt] = useState(null);
  const [email, setEmail] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const isBuyer = lead.type === "buyer" || lead.type === "renter";

  useEffect(() => {
    if (lead.listing_id) crmLeadMarketing(lead.id).then(setMkt).catch(() => {});
  }, [lead.id, lead.listing_id]);

  const saveCrit = async () => {
    const payload = {
      budget_max: crit.budget_max ? Number(crit.budget_max) : null,
      beds_min: crit.beds_min ? Number(crit.beds_min) : null,
      baths_min: crit.baths_min ? Number(crit.baths_min) : null,
      must_haves: crit.must_haves.split(",").map((s) => s.trim()).filter(Boolean),
      city_geo_ids: crit.city_geo_ids,
    };
    try { await crmUpdateLead(lead.id, { criteria: payload }); toast.success("Criteria saved"); onChanged(); return true; }
    catch { toast.error("Couldn't save criteria"); return false; }
  };
  const findMatches = async () => {
    await saveCrit();
    setMatches("loading");
    try { const r = await crmLeadMatches(lead.id); setMatches(r); }
    catch { setMatches({ ok: false, matches: [] }); }
  };
  const draft = async () => {
    setEmailBusy(true);
    try { setEmail(await crmDraftEmail(lead.id)); }
    catch { toast.error("Couldn't draft email"); }
    finally { setEmailBusy(false); }
  };
  const copyEmail = () => {
    if (!email) return;
    navigator.clipboard?.writeText(`Subject: ${email.subject}\n\n${email.body}`);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const setCr = (k) => (e) => setCrit((s) => ({ ...s, [k]: e.target.value }));
  const addNote = async () => {
    if (!note.trim()) return;
    try { const { note: n } = await crmAddNote(lead.id, note); setActive({ ...lead, notes: [...(lead.notes || []), n] }); setNote(""); onChanged(); }
    catch { toast.error("Couldn't add note"); }
  };
  const saveFu = async () => {
    try { await crmUpdateLead(lead.id, { follow_up_date: fu || null }); setActive({ ...lead, follow_up_date: fu }); onChanged(); toast.success("Updated"); }
    catch { toast.error("Couldn't update"); }
  };
  const setStage = async (stage) => {
    try { await crmUpdateLead(lead.id, { stage }); setActive({ ...lead, stage }); onChanged(); }
    catch { toast.error("Couldn't update stage"); }
  };
  const del = async () => {
    try { await crmDeleteLead(lead.id); toast.success("Lead deleted"); onClose(); onChanged(); }
    catch { toast.error("Couldn't delete"); }
  };
  return (
    <Modal onClose={onClose} title={lead.name} testid="lead-detail-modal">
      <div className="space-y-4 text-sm">
        <div className="text-slate-600">{TYPE_LABEL[lead.type]} · Source: {lead.source}{lead.source_detail ? ` (${lead.source_detail})` : ""}</div>
        {(lead.email || lead.phone) && <div className="space-y-1">{lead.email && <div>✉ {lead.email}</div>}{lead.phone && <div>☎ {lead.phone}</div>}</div>}
        <div>
          <div className="mb-1.5 text-xs font-bold uppercase tracking-widest text-slate-500">Stage</div>
          <div className="flex flex-wrap gap-1.5">
            {STAGES.map((s) => (
              <button key={s.id} onClick={() => setStage(s.id)} className={`rounded-full px-3 py-1 text-xs font-bold ${lead.stage === s.id ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`detail-stage-${s.id}`}>{s.label}</button>
            ))}
          </div>
        </div>
        {/* AI follow-up email */}
        <div className="rounded-xl border border-slate-200 p-3">
          <button onClick={draft} disabled={emailBusy} className="flex items-center gap-2 text-sm font-bold text-[#2563EB]" data-testid="detail-draft-email">
            {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Draft follow-up email
          </button>
          {email && (
            <div className="mt-3 space-y-2" data-testid="detail-email">
              <div className="text-xs font-bold text-[#0F172A]">Subject: {email.subject}</div>
              <textarea readOnly value={email.body} rows={6} className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700" />
              <div className="flex items-center gap-2">
                <button onClick={copyEmail} className="flex items-center gap-1 rounded-lg bg-[#0F172A] px-3 py-1.5 text-xs font-bold text-white">{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy</button>
                <span className="text-[10px] text-slate-400">{email.ai ? "AI-generated draft" : "Template draft (AI at usage limit)"}</span>
              </div>
            </div>
          )}
        </div>

        {/* Linked property scans (dynamic QR) */}
        {mkt?.linked && (
          <div className="rounded-xl border border-slate-200 p-3" data-testid="detail-marketing">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500"><QrCode className="h-3.5 w-3.5" /> Linked property</div>
            <div className="mt-1 text-sm font-semibold text-[#0F172A]">{mkt.listing.address} · {mkt.listing.city_name}</div>
            <div className="mt-1 flex items-center gap-4 text-sm text-slate-600">
              <span><b className="text-[#0F172A]">{mkt.stats?.clicks ?? 0}</b> QR scans</span>
              <span className="text-xs text-slate-400">Destination: {mkt.listing.qr_mode}</span>
            </div>
          </div>
        )}

        {/* Buyer criteria + matches */}
        {isBuyer && (
          <div className="rounded-xl border border-slate-200 p-3" data-testid="detail-matching">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500"><Target className="h-3.5 w-3.5" /> Buyer criteria & matches</div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <MiniInp label="Max $" v={crit.budget_max} onChange={setCr("budget_max")} testid="crit-budget" />
              <MiniInp label="Beds+" v={crit.beds_min} onChange={setCr("beds_min")} testid="crit-beds" />
              <MiniInp label="Baths+" v={crit.baths_min} onChange={setCr("baths_min")} testid="crit-baths" />
            </div>
            <MiniInp label="Must-haves (comma)" v={crit.must_haves} onChange={setCr("must_haves")} testid="crit-musthaves" />
            <button onClick={findMatches} className="mt-2 rounded-full bg-[#2563EB] px-4 py-2 text-xs font-bold text-white" data-testid="detail-find-matches">Find matches</button>
            {matches === "loading" && <div className="mt-3 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Scoring…</div>}
            {matches && matches !== "loading" && (
              <div className="mt-3 space-y-2" data-testid="detail-matches">
                {matches.ok && matches.matches.length ? matches.matches.slice(0, 6).map((m) => (
                  <a key={m.id} href={`/homes/property/${m.id}`} target="_blank" rel="noreferrer" className="block rounded-lg border border-slate-200 p-2.5 hover:border-[#2563EB]" data-testid={`match-${m.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-[#0F172A]">{m.address || "Home"} · ${m.price?.toLocaleString()}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${m.score >= 85 ? "bg-emerald-100 text-emerald-700" : m.score >= 55 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{m.label} · {m.score}%</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{m.reasons.slice(0, 3).join(" · ")}</div>
                  </a>
                )) : <p className="text-sm text-slate-500">No matches — add criteria above and try again.</p>}
              </div>
            )}
          </div>
        )}

        <div>
          <div className="mb-1.5 text-xs font-bold uppercase tracking-widest text-slate-500">Follow-up date</div>
          <div className="flex gap-2">
            <input type="date" value={fu} onChange={(e) => setFu(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" data-testid="detail-followup" />
            <button onClick={saveFu} className="rounded-lg bg-[#0F172A] px-3 py-2 text-xs font-bold text-white">Save</button>
          </div>
        </div>
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500"><StickyNote className="h-3.5 w-3.5" /> Notes</div>
          <div className="space-y-1.5" data-testid="detail-notes">
            {(lead.notes || []).map((n, i) => <div key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-slate-700">{n.text}<span className="ml-2 text-[10px] text-slate-400">{n.at?.slice(0, 10)}</span></div>)}
            {!(lead.notes || []).length && <div className="text-slate-400">No notes yet.</div>}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="detail-note-input" />
            <button onClick={addNote} className="rounded-lg bg-[#2563EB] px-3 py-2 text-xs font-bold text-white" data-testid="detail-note-add">Add</button>
          </div>
        </div>
        <button onClick={del} className="flex items-center gap-1.5 text-xs font-bold text-rose-600 hover:underline" data-testid="detail-delete"><Trash2 className="h-3.5 w-3.5" /> Delete lead</button>
      </div>
    </Modal>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`kpi-${label.toLowerCase()}`}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400"><Icon className="h-3.5 w-3.5 text-[#2563EB]" /> {label}</div>
    <div className="mt-1 text-3xl font-black text-[#0F172A]">{value}</div>
  </div>
);
const Modal = ({ title, testid, onClose, children }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
    <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="mb-4 flex items-center justify-between"><h3 className="text-xl font-black text-[#0F172A]">{title}</h3><button onClick={onClose} className="text-slate-400 hover:text-[#0F172A]"><X className="h-5 w-5" /></button></div>
      {children}
    </div>
  </div>
);
const Inp = ({ label, v, onChange, type = "text", testid }) => (
  <div><label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
    <input type={type} value={v} onChange={onChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#2563EB]" data-testid={testid} /></div>
);
const MiniInp = ({ label, v, onChange, testid }) => (
  <div><label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</label>
    <input value={v} onChange={onChange} className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-[#2563EB]" data-testid={testid} /></div>
);
const Sel = ({ label, v, onChange, opts, testid }) => (
  <div><label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
    <select value={v} onChange={onChange} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#2563EB]" data-testid={testid}>
      {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}</select></div>
);
const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;
